import React, { useState, useEffect } from 'react';
import PageExplainer from '@/components/PageExplainer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/RichTextEditor';
import RichText from '@/components/RichText';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Lightbulb, Plus, Clock, CheckCircle, Users, Lock, FlaskConical,
  LayoutDashboard, Target, MessageSquare, TrendingUp, Zap,
  HelpCircle, Wrench, Globe, UserPlus, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import QuickActions from '@/components/QuickActions';

const BACKEND_URL = '';

const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬' },
  { code: 'AE', name: 'UAE', flag: '🇦🇪' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'XX', name: 'Global / Other', flag: '🌍' },
];

const IMPACT_LEVELS = [
  { value: 'individual', label: 'Individual', desc: 'Affects one person or team' },
  { value: 'organisation', label: 'Organisation', desc: 'Affects an entire company' },
  { value: 'industry', label: 'Industry', desc: 'Affects a single sector' },
  { value: 'cross_industry', label: 'Cross Industry', desc: 'Affects multiple sectors' },
];

const getCountryFlag = (countryCode) => {
  const c = COUNTRIES.find(x => x.code === countryCode || x.name === countryCode);
  return c ? c.flag : '';
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-brand-sage/20 text-brand-sage', icon: FlaskConical },
  solved: { label: 'Solved', color: 'bg-brand-blue/20 text-brand-navy', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-600', icon: Lock },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
};

// Visual config for each impact "altitude". Order matters — drives sort
// in the breakdown strip below from local to global so members instantly
// see whether the platform is solving low-altitude vs systemic problems.
const IMPACT_VISUALS = {
  individual:     { label: 'Individual',      color: 'bg-sky-50 text-sky-700 border-sky-200',                bar: 'bg-sky-400',    icon: '👤' },
  organisation:   { label: 'Organisation',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',    bar: 'bg-emerald-500', icon: '🏢' },
  industry:       { label: 'Industry',        color: 'bg-violet-50 text-violet-700 border-violet-200',       bar: 'bg-violet-500',  icon: '🏭' },
  cross_industry: { label: 'Cross Industry',  color: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',    bar: 'bg-fuchsia-500', icon: '🌐' },
};

/**
 * ImpactBreakdown — shows how the population of BriteLabs is distributed
 * across the four "altitudes" of change. Used on both the Labs list
 * (top-of-page summary) and the Dashboard tab (full analytics card).
 *
 * `compact` flips between the strip-style and full-card layouts.
 */
function ImpactBreakdown({ breakdown, unspecified = 0, compact = false }) {
  if (!breakdown || breakdown.length === 0) return null;
  const total = breakdown.reduce((s, b) => s + b.count, 0) + unspecified;
  if (total === 0) return null;

  if (compact) {
    return (
      <Card className="p-3 md:p-4 mb-4 border border-slate-200 bg-white" data-testid="impact-breakdown-strip">
        <div className="flex items-center gap-2 mb-2">
          <Layers className="h-4 w-4 text-brand-navy" />
          <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Level of Impact</h4>
          <span className="text-[10px] text-slate-400">({total} lab{total === 1 ? '' : 's'})</span>
        </div>
        <div className="flex w-full h-2 rounded-full overflow-hidden bg-slate-100 mb-2.5">
          {breakdown.map(b => b.count > 0 && (
            <div
              key={b.level}
              className={`${IMPACT_VISUALS[b.level]?.bar || 'bg-slate-400'} transition-all`}
              style={{ width: `${(b.count / total) * 100}%` }}
              title={`${IMPACT_VISUALS[b.level]?.label}: ${b.count}`}
            />
          ))}
          {unspecified > 0 && (
            <div className="bg-slate-300" style={{ width: `${(unspecified / total) * 100}%` }} title={`Unspecified: ${unspecified}`} />
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {breakdown.map(b => (
            <span key={b.level} data-testid={`impact-chip-${b.level}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${IMPACT_VISUALS[b.level]?.color || 'bg-slate-50 text-slate-600 border-slate-200'} ${b.count === 0 ? 'opacity-40' : ''}`}>
              <span aria-hidden>{IMPACT_VISUALS[b.level]?.icon}</span>
              {IMPACT_VISUALS[b.level]?.label || b.level}
              <span className="font-bold ml-0.5">{b.count}</span>
            </span>
          ))}
          {unspecified > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border bg-slate-50 text-slate-500 border-slate-200">
              Unspecified <span className="font-bold ml-0.5">{unspecified}</span>
            </span>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div data-testid="impact-breakdown-cards">
      <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
        <Layers className="h-4 w-4" /> Level of Impact
        <span className="text-[10px] font-medium text-slate-400 normal-case ml-1">({total} lab{total === 1 ? '' : 's'} categorised)</span>
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {breakdown.map(b => {
          const v = IMPACT_VISUALS[b.level] || {};
          return (
            <Card key={b.level} data-testid={`impact-card-${b.level}`}
              className="p-4 border border-slate-200 bg-white hover:shadow-sm transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border ${v.color || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                  <span aria-hidden>{v.icon}</span> {v.label || b.level}
                </span>
                <span className="text-[10px] text-slate-400">{b.percent}%</span>
              </div>
              <p className="text-2xl font-bold text-slate-800 leading-none">{b.count}</p>
              <p className="text-[11px] text-slate-500 mt-1">
                <span className="font-medium text-brand-sage">{b.active} active</span>
                <span className="mx-1.5 text-slate-300">•</span>
                <span className="font-medium text-brand-navy">{b.solved} solved</span>
              </p>
              <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
                <Users className="h-3 w-3" /> {b.stakeholders} stakeholder{b.stakeholders === 1 ? '' : 's'}
              </p>
            </Card>
          );
        })}
      </div>
      {unspecified > 0 && (
        <p className="text-[11px] text-slate-400 mt-2">
          {unspecified} lab{unspecified === 1 ? '' : 's'} have no impact level set yet.
        </p>
      )}
    </div>
  );
}

function DashboardTab({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/britelabs/dashboard`, { credentials: 'include', headers })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-navy" />
    </div>
  );

  if (!data || data.overview.total === 0) return (
    <Card className="p-12 text-center border border-slate-200">
      <LayoutDashboard className="h-12 w-12 text-slate-300 mx-auto mb-4" />
      <h3 className="text-lg font-semibold text-slate-700 mb-2">No BriteLabs data yet</h3>
      <p className="text-sm text-slate-500">Create or get invited to BriteLabs to see your dashboard metrics.</p>
    </Card>
  );

  const { overview, labs, roi } = data;
  const isAdmin = user?.role === 'admin';
  const impactBreakdown = data.impact_breakdown || [];
  const impactUnspecified = data.impact_unspecified || 0;

  return (
    <div className="space-y-6" data-testid="britelabs-dashboard">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5 border border-slate-200 text-center hover:shadow-md transition-shadow bg-white">
          <div className="w-10 h-10 rounded-xl bg-brand-navy/10 flex items-center justify-center mx-auto mb-2.5">
            <FlaskConical className="h-5 w-5 text-brand-navy" />
          </div>
          <p className="text-3xl font-bold text-slate-800">{overview.total}</p>
          <p className="text-xs text-slate-500 mt-0.5 font-medium">Total Labs</p>
        </Card>
        <Card className="p-5 border border-slate-200 text-center hover:shadow-md transition-shadow bg-white">
          <div className="w-10 h-10 rounded-xl bg-brand-sage/10 flex items-center justify-center mx-auto mb-2.5">
            <Zap className="h-5 w-5 text-brand-sage" />
          </div>
          <p className="text-3xl font-bold text-brand-sage">{overview.active}</p>
          <p className="text-xs text-slate-500 mt-0.5 font-medium">Active</p>
        </Card>
        <Card className="p-5 border border-slate-200 text-center hover:shadow-md transition-shadow bg-white">
          <div className="w-10 h-10 rounded-xl bg-brand-blue/10 flex items-center justify-center mx-auto mb-2.5">
            <CheckCircle className="h-5 w-5 text-brand-navy" />
          </div>
          <p className="text-3xl font-bold text-brand-navy">{overview.solved}</p>
          <p className="text-xs text-slate-500 mt-0.5 font-medium">Solved</p>
        </Card>
        <Card className="p-5 border border-slate-200 text-center hover:shadow-md transition-shadow bg-white">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mx-auto mb-2.5">
            <Clock className="h-5 w-5 text-amber-600" />
          </div>
          <p className="text-3xl font-bold text-amber-700">{overview.pending}</p>
          <p className="text-xs text-slate-500 mt-0.5 font-medium">Pending</p>
        </Card>
      </div>

      {/* Engagement Metrics */}
      <div>
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> Engagement Metrics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="p-4 border border-slate-200 bg-white hover:shadow-sm transition-shadow">
            <p className="text-xs text-slate-500 mb-1.5 font-medium">Avg Time to Solve</p>
            <p className="text-xl font-bold text-slate-800">{roi.avg_time_to_solution > 0 ? `${roi.avg_time_to_solution}d` : '--'}</p>
          </Card>
          <Card className="p-4 border border-slate-200 bg-white hover:shadow-sm transition-shadow">
            <p className="text-xs text-slate-500 mb-1.5 font-medium">Avg Engagement / Lab</p>
            <p className="text-xl font-bold text-slate-800">{roi.avg_engagement_rate > 0 ? roi.avg_engagement_rate : '--'}</p>
          </Card>
          <Card className="p-4 border border-slate-200 bg-white hover:shadow-sm transition-shadow">
            <p className="text-xs text-slate-500 mb-1.5 font-medium">Total Messages</p>
            <p className="text-xl font-bold text-slate-800">{roi.total_messages}</p>
          </Card>
          <Card className="p-4 border border-slate-200 bg-white hover:shadow-sm transition-shadow">
            <p className="text-xs text-slate-500 mb-1.5 font-medium">Milestones Done</p>
            <p className="text-xl font-bold text-slate-800">{roi.total_milestones_done}</p>
          </Card>
          <Card className="p-4 border border-slate-200 bg-white hover:shadow-sm transition-shadow">
            <p className="text-xs text-slate-500 mb-1.5 font-medium">Total Stakeholders</p>
            <p className="text-xl font-bold text-slate-800">{roi.total_stakeholders}</p>
          </Card>
        </div>
      </div>

      {/* Impact Level Breakdown */}
      <ImpactBreakdown breakdown={impactBreakdown} unspecified={impactUnspecified} />

      {/* Lab-by-Lab Breakdown */}
      <div>
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
          <Target className="h-4 w-4" /> Lab Progress {isAdmin && <Badge variant="outline" className="text-xs ml-1">All Labs</Badge>}
        </h3>
        <div className="space-y-3">
          {labs.map(lab => {
            const sc = STATUS_CONFIG[lab.status] || STATUS_CONFIG.active;
            const milestonePct = lab.milestones_total > 0 ? Math.round((lab.milestones_done / lab.milestones_total) * 100) : 0;

            return (
              <Card key={lab.lab_id} data-testid={`dashboard-lab-${lab.lab_id}`}
                className="p-4 border border-slate-200 hover:border-brand-blue/50 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/britelabs/${lab.lab_id}`)}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h4 className="font-semibold text-sm text-slate-800 truncate">{lab.title}</h4>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${sc.color}`}>
                        {sc.label}
                      </span>
                      {isAdmin && <span className="text-[10px] text-slate-400">Owner: {lab.creator_name}</span>}
                    </div>

                    {/* Progress bar for milestones */}
                    <div className="flex items-center gap-3 mt-2">
                      <div className="flex-1 max-w-xs">
                        <div className="flex justify-between text-[10px] text-slate-500 mb-0.5">
                          <span>Milestones</span>
                          <span>{lab.milestones_done}/{lab.milestones_total}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-brand-navy to-brand-sage rounded-full transition-all"
                            style={{ width: `${lab.milestones_total > 0 ? milestonePct : 0}%` }} />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right stats */}
                  <div className="flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
                    <div className="text-center">
                      <p className="font-bold text-sm text-slate-700">{lab.stakeholder_count}</p>
                      <p className="text-[10px]"><Users className="h-3 w-3 inline" /></p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-sm text-slate-700">{lab.message_count}</p>
                      <p className="text-[10px]"><MessageSquare className="h-3 w-3 inline" /></p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-sm text-slate-700">{lab.days_open}d</p>
                      <p className="text-[10px]"><Clock className="h-3 w-3 inline" /></p>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function BriteLabsPage({ user }) {
  const [labs, setLabs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({ root_cause: '', lab_type: 'problem', title: '', problem_statement: '', category: '', desired_outcome: '', country: '', impact_level: '' });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    loadLabs();
    fetch(`${BACKEND_URL}/api/britelabs/categories`, { credentials: 'include', headers })
      .then(r => r.json()).then(setCategories).catch(() => {});
  }, []);

  // Auto-open create dialog when navigated from forum post
  useEffect(() => {
    if (searchParams.get('request') === 'true') {
      const title = searchParams.get('title') || '';
      const problem = searchParams.get('problem') || '';
      setForm(f => ({ ...f, title, problem_statement: problem }));
      setShowCreate(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  const loadLabs = () => {
    fetch(`${BACKEND_URL}/api/britelabs`, { credentials: 'include', headers })
      .then(r => r.json())
      .then(data => { setLabs(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  const handleCreate = async () => {
    const effectiveCategory = form.category === 'Other' && form.customCategory?.trim()
      ? form.customCategory.trim()
      : form.category;
    if (!form.root_cause || !form.title || !form.problem_statement || !effectiveCategory) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, category: effectiveCategory };
      delete payload.customCategory;
      const res = await fetch(`${BACKEND_URL}/api/britelabs`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('BriteLab request submitted! An admin will review it shortly.');
      setShowCreate(false);
      setForm({ root_cause: '', lab_type: 'problem', title: '', problem_statement: '', category: '', desired_outcome: '', country: '', impact_level: '' });
      loadLabs();
    } catch {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = filter === 'all' ? labs : labs.filter(l => l.status === filter);

  // Client-side aggregation of impact levels for the Labs-tab summary
  // strip. We can't reuse the /britelabs/dashboard endpoint here (the
  // Labs tab uses /api/britelabs which returns full lab docs including
  // visibility-restricted ones), so we recompute the same shape locally.
  const labsImpactBreakdown = (() => {
    const IMPACT_KEYS = ['individual', 'organisation', 'industry', 'cross_industry'];
    const counts = Object.fromEntries(IMPACT_KEYS.map(k => [k, { count: 0, active: 0, solved: 0, stakeholders: 0 }]));
    let unspecified = 0;
    labs.forEach(l => {
      const lvl = (l.impact_level || '').trim().toLowerCase();
      if (!IMPACT_KEYS.includes(lvl)) { unspecified += 1; return; }
      counts[lvl].count += 1;
      if (l.status === 'active') counts[lvl].active += 1;
      if (l.status === 'solved') counts[lvl].solved += 1;
      counts[lvl].stakeholders += (l.kpis?.stakeholder_count || 1);
    });
    const total = labs.length;
    return {
      breakdown: IMPACT_KEYS.map(k => ({
        level: k,
        label: IMPACT_LEVELS.find(il => il.value === k)?.label || k,
        count: counts[k].count,
        active: counts[k].active,
        solved: counts[k].solved,
        stakeholders: counts[k].stakeholders,
        percent: total > 0 ? Math.round((counts[k].count / total) * 100 * 10) / 10 : 0,
      })),
      unspecified,
    };
  })();

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="britelab" type="top" className="mb-4 md:mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4 md:mb-6">
            <div>
              <h1 className="text-2xl md:text-4xl sm:text-5xl font-bold font-heading mb-1 md:mb-2 flex items-center gap-2">
                <span className="gradient-text">BriteLabs</span>
                <PageExplainer pageKey="britelab" label="About BriteLab" />
              </h1>
              <p className="text-sm md:text-base text-slate-600 max-w-xl">
                Collaborative problem-solving spaces. Propose a challenge, invite stakeholders, and co-create solutions.
              </p>
            </div>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button data-testid="create-britelab-btn" className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                  <Plus className="h-4 w-4 mr-2" /> Request BriteLab
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Request a BriteLab</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  {/* Step 1: Type — Question or Problem */}
                  <div>
                    <Label>Is this a... *</Label>
                    <div className="flex gap-3 mt-2">
                      <button type="button" data-testid="britelab-type-question"
                        onClick={() => setForm({ ...form, lab_type: 'question' })}
                        className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                          form.lab_type === 'question' ? 'border-brand-navy bg-brand-navy/5' : 'border-slate-200 hover:border-slate-300'
                        }`}>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          form.lab_type === 'question' ? 'border-brand-navy' : 'border-slate-300'
                        }`}>
                          {form.lab_type === 'question' && <div className="w-2 h-2 rounded-full bg-brand-navy" />}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-1.5">
                            <HelpCircle className="h-4 w-4 text-brand-navy" />
                            <span className="text-sm font-semibold text-slate-800">Question to be answered</span>
                          </div>
                        </div>
                      </button>
                      <button type="button" data-testid="britelab-type-problem"
                        onClick={() => setForm({ ...form, lab_type: 'problem' })}
                        className={`flex-1 flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                          form.lab_type === 'problem' ? 'border-brand-sage bg-brand-sage/5' : 'border-slate-200 hover:border-slate-300'
                        }`}>
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          form.lab_type === 'problem' ? 'border-brand-sage' : 'border-slate-300'
                        }`}>
                          {form.lab_type === 'problem' && <div className="w-2 h-2 rounded-full bg-brand-sage" />}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-1.5">
                            <Wrench className="h-4 w-4 text-brand-sage" />
                            <span className="text-sm font-semibold text-slate-800">Problem to be solved</span>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <Label>Title *</Label>
                    <p className="text-xs text-slate-400 mt-0.5 mb-1">List your question or problem to solve here</p>
                    <Input data-testid="britelab-title-input" placeholder="e.g., Reducing last-mile emissions in urban areas"
                      value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                  </div>

                  {/* Root Cause */}
                  <div>
                    <Label>Define Root Cause *</Label>
                    <RichTextEditor value={form.root_cause}
                      onChange={v => setForm({ ...form, root_cause: v })}
                      placeholder="What is the root cause of the issue you want to address?"
                      testId="britelab-root-cause-input" minHeight="5rem" />
                  </div>

                  {/* Category */}
                  <div>
                    <Label>Category *</Label>
                    <Select value={form.category} onValueChange={v => setForm({ ...form, category: v, customCategory: '' })}>
                      <SelectTrigger data-testid="britelab-category-select" className="mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {form.category === 'Other' && (
                      <Input placeholder="Enter custom category" value={form.customCategory || ''}
                        onChange={e => setForm({ ...form, customCategory: e.target.value })}
                        className="mt-2" data-testid="britelab-custom-category" />
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {categories.slice(0, 8).map(c => (
                        <button key={c} type="button" onClick={() => setForm({ ...form, category: c, customCategory: '' })}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-all ${form.category === c ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-500 border-slate-200 hover:border-brand-blue'}`}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <Label>Country</Label>
                    <Select value={form.country} onValueChange={v => setForm({ ...form, country: v })}>
                      <SelectTrigger data-testid="britelab-country-select" className="mt-1"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Impact Level */}
                  <div>
                    <Label>Level of Impact if Solution Created</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {IMPACT_LEVELS.map(il => (
                        <button key={il.value} type="button" data-testid={`impact-${il.value}`}
                          onClick={() => setForm({ ...form, impact_level: il.value })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            form.impact_level === il.value ? 'border-brand-navy bg-brand-navy/5' : 'border-slate-200 hover:border-slate-300'
                          }`}>
                          <span className="text-sm font-semibold text-slate-800 block">{il.label}</span>
                          <span className="text-[11px] text-slate-400">{il.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Problem Statement */}
                  <div>
                    <Label>Problem Statement *</Label>
                    <RichTextEditor value={form.problem_statement}
                      onChange={v => setForm({ ...form, problem_statement: v })}
                      placeholder="Describe the problem you want to solve..."
                      testId="britelab-problem-input" minHeight="7rem" />
                  </div>

                  {/* Desired Outcome */}
                  <div>
                    <Label>Desired Outcome</Label>
                    <RichTextEditor value={form.desired_outcome}
                      onChange={v => setForm({ ...form, desired_outcome: v })}
                      placeholder="What does success look like?"
                      testId="britelab-outcome-input" minHeight="6rem" />
                  </div>

                  <Button data-testid="submit-britelab-btn" onClick={handleCreate} disabled={submitting}
                    className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                    {submitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                  <p className="text-xs text-slate-400 text-center">Your request will be reviewed by an admin before activation.</p>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Main Tabs: Labs | Dashboard */}
          <Tabs defaultValue="dashboard">
            <TabsList className="mb-6">
              <TabsTrigger data-testid="tab-labs" value="labs">
                <FlaskConical className="h-4 w-4 mr-1.5" /> Labs
              </TabsTrigger>
              <TabsTrigger data-testid="tab-dashboard" value="dashboard">
                <LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard
              </TabsTrigger>
            </TabsList>

            {/* Labs Tab */}
            <TabsContent value="labs">
              {/* Level of impact summary */}
              {labs.length > 0 && (
                <ImpactBreakdown
                  breakdown={labsImpactBreakdown.breakdown}
                  unspecified={labsImpactBreakdown.unspecified}
                  compact
                />
              )}

              {/* Filters */}
              <div className="flex gap-2 mb-6 flex-wrap">
                {[{ key: 'all', label: 'All' }, { key: 'active', label: 'Active' }, { key: 'solved', label: 'Solved' }, { key: 'pending', label: 'Pending' }].map(f => (
                  <button key={f.key} data-testid={`filter-${f.key}`}
                    onClick={() => setFilter(f.key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === f.key ? 'bg-brand-navy text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-brand-blue'}`}>
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Labs list */}
              {loading ? (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-navy mx-auto" />
                </div>
              ) : filtered.length === 0 ? (
                <Card className="p-12 text-center">
                  <Lightbulb className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No BriteLabs yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Be the first to propose a problem to solve collaboratively.</p>
                  <Button onClick={() => setShowCreate(true)} variant="outline">
                    <Plus className="h-4 w-4 mr-2" /> Request a BriteLab
                  </Button>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {filtered.map(lab => {
                    const sc = STATUS_CONFIG[lab.status] || STATUS_CONFIG.active;
                    const StatusIcon = sc.icon;
                    return (
                      <Card key={lab.lab_id} data-testid={`britelab-card-${lab.lab_id}`}
                        className={`p-6 hover:shadow-lg transition-all cursor-pointer border ${
                          lab.my_invite_status === 'pending_invite'
                            ? 'border-amber-300 bg-amber-50/30 hover:border-amber-400'
                            : 'border-slate-200 hover:border-brand-blue/50'
                        }`}
                        onClick={() => navigate(`/britelabs/${lab.lab_id}`)}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {lab.my_invite_status === 'pending_invite' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                  <UserPlus className="h-3 w-3" /> Invitation Pending
                                </span>
                              )}
                              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${sc.color}`}>
                                <StatusIcon className="h-3 w-3" /> {sc.label}
                              </span>
                              {lab.lab_type && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                  lab.lab_type === 'question' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
                                }`}>
                                  {lab.lab_type === 'question' ? <HelpCircle className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
                                  {lab.lab_type === 'question' ? 'Question' : 'Problem'}
                                </span>
                              )}
                              <Badge variant="outline" className="text-xs">{lab.category}</Badge>
                              {lab.impact_level && (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100">
                                  {IMPACT_LEVELS.find(il => il.value === lab.impact_level)?.label || lab.impact_level}
                                </span>
                              )}
                              {lab.country && (
                                <span className="text-base leading-none" title={COUNTRIES.find(c => c.code === lab.country)?.name}>
                                  {getCountryFlag(lab.country)}
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg font-bold font-heading text-slate-800 mb-1">{lab.title}</h3>
                            <div className="text-sm text-slate-500"><RichText html={lab.problem_statement} className="card card-2" /></div>
                            <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {lab.kpis?.stakeholder_count || 1} stakeholders</span>
                              {lab.milestones?.length > 0 && (
                                <span className="flex items-center gap-1">
                                  <Target className="h-3.5 w-3.5" />
                                  {lab.milestones.filter(m => m.status === 'complete').length}/{lab.milestones.length} milestones
                                </span>
                              )}
                              <span>Owner: {lab.creator_name}</span>
                              <span>{new Date(lab.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard">
              <DashboardTab user={user} />
            </TabsContent>
          </Tabs>

          <QuickActions user={user} exclude={['/britelabs']} />
            </div>
            <PageSideRail placement="britelab" />
          </div>
        </div>
      </main>
    </div>
  );
}
